import { ClientProfileContentItem } from 'store/clientProfile/clientProfileSlice';
import { Status as ClientStatus } from 'store/clients/clientsSlice';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { NoteForm } from '../NoteForm';
import { FinalizedNoteFormProvider, useNoteFormContext } from '../context';
import { AiScribeMedia, FinalizedEditActions } from './NoteFormSections';

type FinalNoteFormProps = {
  status: ClientStatus | ClientStatus['id'];
  clientId: string;
  clientName: string;
  prependedClientId: string;
  note?: ClientProfileContentItem;
  disabled?: boolean;
  onCancel?: () => void;
  onSubmitSuccess?: () => void;
};

const FinalNoteFormContent = ({ onCancel }: { onCancel?: () => void }) => {
  const { enableDateTimeUpdates } = useFlags();
  const { isManagerReview, disabled } = useNoteFormContext();

  return (
    <NoteForm.Form>
      <NoteForm.FieldsContainer expanded={true}>
        <NoteForm.FieldsContainer.Row>
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.Fields.NoteType disabled={disabled} />
          </NoteForm.FieldsContainer.HalfRow>
          <NoteForm.FieldsContainer.HalfRow>
            <NoteForm.Fields.DateOfEncounter disabled={disabled} />
          </NoteForm.FieldsContainer.HalfRow>
        </NoteForm.FieldsContainer.Row>

        {enableDateTimeUpdates ? (
          <NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.StartTime disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
            <NoteForm.FieldsContainer.HalfRow>
              <NoteForm.Fields.TimeSpent disabled={disabled} />
            </NoteForm.FieldsContainer.HalfRow>
          </NoteForm.FieldsContainer.Row>
        ) : (
          !isManagerReview && (
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.StartTime disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.TimeSpent disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
          )
        )}

        {isManagerReview && (
          <>
            {enableDateTimeUpdates ? (
              <>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Manager disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Chw disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                </NoteForm.FieldsContainer.Row>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.FullWidth>
                    <NoteForm.Fields.Provider disabled={disabled} />
                  </NoteForm.FieldsContainer.FullWidth>
                </NoteForm.FieldsContainer.Row>
              </>
            ) : (
              <>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.TimeSpent disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Manager disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                </NoteForm.FieldsContainer.Row>
                <NoteForm.FieldsContainer.Row>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Chw disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                  <NoteForm.FieldsContainer.HalfRow>
                    <NoteForm.Fields.Provider disabled={disabled} />
                  </NoteForm.FieldsContainer.HalfRow>
                </NoteForm.FieldsContainer.Row>
              </>
            )}
          </>
        )}

        {!isManagerReview && (
          <>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.NextContact disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.Row>
              <NoteForm.FieldsContainer.HalfRow>
                <NoteForm.Fields.WhoDidYouReach disabled={disabled} />
              </NoteForm.FieldsContainer.HalfRow>
            </NoteForm.FieldsContainer.Row>
            <NoteForm.FieldsContainer.FullWidth>
              <NoteForm.Fields.TypeOfSupport disabled={disabled} />
            </NoteForm.FieldsContainer.FullWidth>
          </>
        )}

        <AiScribeMedia />
        <NoteForm.FieldsContainer.HalfRow>
          <NoteForm.Fields.Note disabled={disabled} />
        </NoteForm.FieldsContainer.HalfRow>
        <NoteForm.FieldsContainer.HalfRow>
          <NoteForm.Fields.DocumentationTime disabled={disabled} />
        </NoteForm.FieldsContainer.HalfRow>
      </NoteForm.FieldsContainer>
      <FinalizedEditActions onCancel={onCancel} />
    </NoteForm.Form>
  );
};

/**
 * Preset component for editing finalized notes.
 * Used in newNoteView when editing a final note.
 * Has Cancel and Final buttons (no delete or edit).
 * No autosave since this is for finalized notes.
 */
export const FinalNoteForm = ({
  status,
  clientId,
  note,
  clientName,
  prependedClientId,
  disabled = false,
  onCancel,
  onSubmitSuccess
}: FinalNoteFormProps) => {
  return (
    <FinalizedNoteFormProvider
      status={status}
      clientId={clientId}
      note={note}
      disabled={disabled}
      clientName={clientName}
      prependedClientId={prependedClientId}
      onSubmitSuccess={onSubmitSuccess}
    >
      <NoteForm.Root>
        <FinalNoteFormContent onCancel={onCancel} />
      </NoteForm.Root>
    </FinalizedNoteFormProvider>
  );
};
